export const INNER_R = 44
export const OUTER_R = 96
export const MID_R = 70
export const WHEEL_SIZE = 204
export const CENTER = 102
export const SEG_GAP_DEG = 3
export const EDGE_MARGIN = 14
export const MAX_SLOTS = 12
export const SLOT_DEG = 30
export const MIN_COVERAGE = 0.66

export type OrbitSegment = {
  startDeg: number
  endDeg: number
  midDeg: number
  iconX: number
  iconY: number
}

const viewportWidth = () => (typeof window === 'undefined' ? WHEEL_SIZE : window.innerWidth)
const viewportHeight = () => (typeof window === 'undefined' ? WHEEL_SIZE : window.innerHeight)

export const baseCoverage = (count: number) =>
  count >= MAX_SLOTS ? 1 : Math.max(MIN_COVERAGE, count / MAX_SLOTS)

// Ported from the reference mock: sample the outer edge, then select the
// longest contiguous run that can remain visible inside the viewport margin.
export const orientArc = (
  cursorX: number,
  cursorY: number,
  viewportW = viewportWidth(),
  viewportH = viewportHeight(),
) => {
  const safe = Array.from({ length: 180 }, (_, index) => {
    const radians = ((index * 2 - 90) * Math.PI) / 180
    const x = cursorX + OUTER_R * Math.cos(radians)
    const y = cursorY + OUTER_R * Math.sin(radians)
    return x >= EDGE_MARGIN && x <= viewportW - EDGE_MARGIN && y >= EDGE_MARGIN && y <= viewportH - EDGE_MARGIN
  })

  const doubled = safe.concat(safe)
  let bestStart = 0
  let bestLength = 0
  let runStart = -1

  doubled.forEach((isSafe, index) => {
    if (!isSafe) {
      runStart = -1
      return
    }
    if (runStart < 0) runStart = index
    const length = index - runStart + 1
    if (length > bestLength) {
      bestLength = length
      bestStart = runStart
    }
  })

  if (bestLength >= 180) {
    return {
      centerDeg: 0,
      halfSpanDeg: 180,
    }
  }

  if (bestLength <= 0) {
    const toCenterX = (viewportW / 2) - cursorX
    const toCenterY = (viewportH / 2) - cursorY
    const fallbackCenterDeg = ((Math.atan2(toCenterY, toCenterX) * 180) / Math.PI + 90 + 360) % 360
    return {
      centerDeg: Math.round(fallbackCenterDeg),
      halfSpanDeg: 0,
    }
  }

  const halfSpanDeg = bestLength - 1
  const centerDeg = ((bestStart * 2) + halfSpanDeg) % 360

  return {
    centerDeg,
    halfSpanDeg,
  }
}

export const polarSegments = (
  count: number,
  cursorX: number,
  cursorY: number,
  viewportW?: number,
  viewportH?: number,
): OrbitSegment[] => {
  if (count <= 0) return []

  const { centerDeg, halfSpanDeg } = orientArc(cursorX, cursorY, viewportW, viewportH)
  const desiredHalf = baseCoverage(count) * 180
  const effectiveHalf = halfSpanDeg > 0
    ? Math.min(desiredHalf, halfSpanDeg)
    : Math.max(15, (count * 20) / 2)
  const step = (effectiveHalf * 2) / count
  const startArc = centerDeg - effectiveHalf

  const gap = Math.min(SEG_GAP_DEG, Math.max(0, step * 0.25))

  return Array.from({ length: count }, (_, index) => {
    const startDeg = startArc + index * step + gap / 2
    const endDeg = startArc + (index + 1) * step - gap / 2
    const midDeg = (startDeg + endDeg) / 2
    const radians = ((midDeg - 90) * Math.PI) / 180
    return {
      startDeg,
      endDeg,
      midDeg,
      iconX: CENTER + MID_R * Math.cos(radians),
      iconY: CENTER + MID_R * Math.sin(radians),
    }
  })
}

export const arcD = (startDeg: number, endDeg: number, innerR = INNER_R, outerR = OUTER_R) => {
  const startRadians = ((startDeg - 90) * Math.PI) / 180
  const endRadians = ((endDeg - 90) * Math.PI) / 180
  const point = (radius: number, radians: number) => [CENTER + radius * Math.cos(radians), CENTER + radius * Math.sin(radians)]
  const [innerStartX, innerStartY] = point(innerR, startRadians)
  const [outerStartX, outerStartY] = point(outerR, startRadians)
  const [outerEndX, outerEndY] = point(outerR, endRadians)
  const [innerEndX, innerEndY] = point(innerR, endRadians)
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0

  return `M ${innerStartX.toFixed(2)} ${innerStartY.toFixed(2)} L ${outerStartX.toFixed(2)} ${outerStartY.toFixed(2)} A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEndX.toFixed(2)} ${outerEndY.toFixed(2)} L ${innerEndX.toFixed(2)} ${innerEndY.toFixed(2)} A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStartX.toFixed(2)} ${innerStartY.toFixed(2)} Z`
}

export const iconInto = (x: number, y: number) => `translate(${x - 11} ${y - 11}) scale(${22 / 24})`
